"use client";

import { useRef, useState } from "react";
import {
  ErrorNotice,
  Notice,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, BUTTON_SMALL } from "@/components/tool-kit/styles";
import {
  TestEditor,
  type TestInput,
} from "@/components/tools/stats-clinical/post-test-inputs";
import { PostTestSummary } from "@/components/tools/stats-clinical/post-test-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  chainTests,
  likelihoodRatios,
} from "@/lib/tool-kit/stats-clinical/post-test";

/** English strings. A translated content file overrides these through `ui`. */
export const POST_TEST_PROBABILITY_CALCULATOR_STRINGS = {
  preLabel: "Pre-test probability (%)",
  preHint:
    "How likely the condition is before the test: the prevalence in people like this patient, or your clinical estimate. Between 0 and 100, exclusive.",
  prePlaceholder: "20",
  testHeading: "Test {number}",
  describeLabel: "Describe the test by",
  modeSensSpec: "Sensitivity and specificity",
  modeLr: "A likelihood ratio",
  sensitivity: "Sensitivity (%)",
  specificity: "Specificity (%)",
  sensitivityPlaceholder: "90",
  specificityPlaceholder: "80",
  resultLabel: "Result of the test",
  resultPositive: "Positive",
  resultNegative: "Negative",
  lrLabel: "Likelihood ratio to apply",
  lrHint: "Enter LR+ if the result was positive, or LR− if it was negative.",
  lrPlaceholder: "4.5",
  removeTest: "Remove this test",
  addTest: "Add another test",
  loadExample: "Load example",
  clear: "Clear",
  preInvalid:
    "The pre-test probability must be a number above 0 and below 100.",
  testInvalid:
    "Check test {number}: percentages must be from 0 to 100, a likelihood ratio cannot be negative, and a test needs both a sensitivity and a specificity.",
  contradictory:
    "These likelihood ratios cannot be combined: one is exactly 0 and another is infinite.",
  enterPrompt:
    "Enter a pre-test probability and a test result to get the post-test probability.",
  results: "Results",
  cardPost: "Post-test probability",
  cardOdds: "Post-test odds",
  cardLr: "Combined likelihood ratio",
  reading:
    "Starting from a pre-test probability of {pre}, these results give a post-test probability of {post}: of 100 people with this starting probability and these results, about {perHundred} would have the condition.",
  independenceNote:
    "Chaining multiplies the likelihood ratios, which is valid only if the tests are independent of each other once you know whether the condition is present. Two tests that measure much the same thing are not independent, and the result then overstates the evidence.",
  columnStep: "Step",
  columnLr: "Likelihood ratio",
  columnOdds: "Odds",
  columnProbability: "Probability",
  stepStart: "Before testing",
  stepLr: "Test {number}: {result}, LR {lr}",
  stepLrPositive: "positive",
  stepLrNegative: "negative",
  stepLrGiven: "likelihood ratio",
  nomogramHeading: "Fagan nomogram",
  nomogramNote:
    "A straight line from the pre-test probability on the left through the likelihood ratio in the middle meets the post-test probability on the right.",
  nomogramNoteCombined:
    "The line runs from the starting probability through the combined likelihood ratio of all your tests to the final post-test probability.",
  nomogramLabel:
    "Fagan nomogram: a line from a pre-test probability of {pre} to a post-test probability of {post}",
  axisPre: "Pre-test probability (%)",
  axisRatio: "Likelihood ratio",
  axisPost: "Post-test probability (%)",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  howCalculated: "How this was calculated",
  workingPre: "Pre-test odds = p / (1 − p) = {p} / (1 − {p}) = {odds}",
  workingPost: "Post-test odds = pre-test odds × LR = {pre} × {lr} = {odds}",
  workingProbability:
    "Post-test probability = odds / (1 + odds) = {odds} / (1 + {odds}) = {p}",
  report:
    "Pre-test probability {pre}; after {count} test result(s) with a combined likelihood ratio of {lr}, post-test probability {post}.",
  copyResult: "Copy result",
  copied: "Copied!",
} as const;

const MAX_TESTS = 5;

const BLANK: Omit<TestInput, "id"> = {
  mode: "ss",
  sensitivity: "",
  specificity: "",
  lr: "",
  result: "positive",
};

interface ParsedTest {
  lr: number;
  mode: TestInput["mode"];
  result: TestInput["result"];
}

function parseTest(test: TestInput): ParsedTest | null {
  if (test.mode === "lr") {
    const lr = parseNumberInput(test.lr);
    return lr !== null && lr >= 0
      ? { lr, mode: "lr", result: test.result }
      : null;
  }
  const se = parseNumberInput(test.sensitivity);
  const sp = parseNumberInput(test.specificity);
  if (se === null || sp === null || se < 0 || se > 100 || sp < 0 || sp > 100) {
    return null;
  }
  const lr = likelihoodRatios(se / 100, sp / 100)[test.result];
  return Number.isNaN(lr) ? null : { lr, mode: "ss", result: test.result };
}

function isBlank(test: TestInput): boolean {
  return test.sensitivity === "" && test.specificity === "" && test.lr === "";
}

export default function PostTestProbabilityCalculatorWidget() {
  const strings = useToolUi(POST_TEST_PROBABILITY_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const nextId = useRef(1);
  const [preText, setPreText] = useState("");
  const [tests, setTests] = useState<TestInput[]>([{ id: 0, ...BLANK }]);

  const update = (id: number, patch: Partial<TestInput>) =>
    setTests((current) =>
      current.map((test) => (test.id === id ? { ...test, ...patch } : test))
    );
  const remove = (id: number) =>
    setTests((current) => current.filter((test) => test.id !== id));
  const add = () => {
    const id = nextId.current;
    nextId.current += 1;
    setTests((current) => [...current, { id, ...BLANK }]);
  };

  const pre = parseNumberInput(preText);
  const preValid = pre !== null && pre > 0 && pre < 100;
  const parsed = tests.map(parseTest);
  const firstBad = tests.findIndex(
    (test, i) => !isBlank(test) && parsed[i] === null
  );
  const anyBlank = tests.some(isBlank);
  const ready = preValid && !anyBlank && firstBad < 0;
  const ratios = parsed.flatMap((p) => (p ? [p.lr] : []));
  const result = ready && pre !== null ? chainTests(pre / 100, ratios) : null;
  const stepLabels = parsed.map((p, i) => {
    const description =
      p?.mode === "ss"
        ? strings[p.result === "positive" ? "stepLrPositive" : "stepLrNegative"]
        : strings.stepLrGiven;
    return t(strings.stepLr, {
      number: fmt(i + 1),
      result: description,
      lr: p ? fmt(p.lr, { maximumFractionDigits: 3 }) : "",
    });
  });
  const entered = preText.trim() !== "" || tests.some((test) => !isBlank(test));

  return (
    <div className="space-y-6">
      <TextField
        hint={strings.preHint}
        id="ptp-pre"
        label={strings.preLabel}
        numeric
        onChange={setPreText}
        placeholder={strings.prePlaceholder}
        value={preText}
      />
      {tests.map((test, index) => (
        <TestEditor
          canRemove={tests.length > 1}
          formatNumber={(value) => fmt(value)}
          index={index}
          key={test.id}
          onChange={(patch) => update(test.id, patch)}
          onRemove={() => remove(test.id)}
          strings={strings}
          test={test}
        />
      ))}
      <div className="flex flex-wrap gap-3">
        {tests.length < MAX_TESTS ? (
          <button className={BUTTON_SECONDARY} onClick={add} type="button">
            {strings.addTest}
          </button>
        ) : null}
        <button
          className={BUTTON_SMALL}
          onClick={() => {
            setPreText("20");
            setTests([
              {
                id: 0,
                mode: "ss",
                sensitivity: "90",
                specificity: "80",
                lr: "",
                result: "positive",
              },
              {
                id: nextId.current,
                mode: "ss",
                sensitivity: "85",
                specificity: "95",
                lr: "",
                result: "positive",
              },
            ]);
            nextId.current += 1;
          }}
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => {
            setPreText("");
            setTests([{ id: nextId.current, ...BLANK }]);
            nextId.current += 1;
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {preText.trim() !== "" && !preValid ? (
        <ErrorNotice>{strings.preInvalid}</ErrorNotice>
      ) : null}
      {firstBad >= 0 ? (
        <ErrorNotice>
          {t(strings.testInvalid, { number: fmt(firstBad + 1) })}
        </ErrorNotice>
      ) : null}
      {ready && !result ? (
        <ErrorNotice>{strings.contradictory}</ErrorNotice>
      ) : null}
      {result ? (
        <PostTestSummary
          fmt={fmt}
          result={result}
          stepLabels={stepLabels}
          strings={strings}
          svgRef={svgRef}
        />
      ) : null}
      {entered && !result && preValid && firstBad < 0 && anyBlank ? (
        <Notice>{strings.enterPrompt}</Notice>
      ) : null}
      {entered ? null : <Notice>{strings.enterPrompt}</Notice>}
    </div>
  );
}
